/**
 * The issue vocabulary (§7.1, §9.5), mirrored value-for-value from `pkg/enums/IssueType` and
 * `pkg/enums/IssueSeverity`.
 *
 * # Sixteen types, and the count is the specification
 *
 * §7.1 lists seven families; the plan's W11.2 row enumerates the full sixteen, and the Go package
 * returns them in that order from `All()` so a test can assert the set rather than trusting sixteen
 * constants to have been remembered. This file is the browser half of the same set, and the i18n
 * parity check (`yarn check:i18n`, W11.6) is what makes forgetting one of them fail rather than
 * render an operator a key path during an incident.
 *
 * # A type is a localization key, which is why a value is never re-spelled
 *
 * §9.5: the server never sends prose — it sends `type` + `params{}` and the SPA renders
 * `t('issues.' + type, params)`. A Go-side English sentence cannot be localized into sl, so these
 * strings are simultaneously the wire value, the database `_id`'s middle segment and the message
 * key. Renaming one is a migration, not an edit.
 *
 * Nothing here enforces anything: every renderer over these falls back to the raw value, so a panel
 * one release behind its fleet shows an unknown type as the word rather than as a blank row.
 */

/** §7.1's sixteen, in the order W11.2 enumerates them. */
export enum IssueType {
    // The host-pressure trio (D60). One issue per pressure, never a combined "host unhealthy":
    // CPU, RAM and disk have different causes, different fixes and different people to call.
    HostCPU    = 'host_cpu',
    HostMemory = 'host_memory',
    /** Per **mount** — the one host issue with a real subject: a full `/` and a full `/var/lib/docker` are different incidents. */
    HostDisk   = 'host_disk',

    // The certificate pair (§4.6). Two types rather than one graded type: "renewal is failing" and
    // "the certificate has lapsed" are different incidents, and a severity alone cannot say which.
    CertExpiring = 'cert_expiring',
    CertExpired  = 'cert_expired',

    /** D7's silence. Warning at the lower boundary on purpose (§6.2): the site keeps serving from cache — the panel's *visibility* is what is down. */
    InstanceOffline = 'instance_offline',

    /** ADR-0007's delivered-vs-applied gap, and nothing else. */
    StaleAppliedVersion = 'stale_applied_version',

    TagMismatch   = 'tag_mismatch',
    OutdatedAgent = 'outdated_agent',

    /** ADR-0007's *applied* fact turned false. No warning grade at all — the site is not serving what the panel published. */
    ReloadFailure = 'reload_failure',
    /** ADR-0007's *healthy* fact. Warning, permanently if need be: one decommissioned backend must not light the "deployments are stuck" alarm forever. */
    RouterServiceErrors = 'router_service_errors',

    ClockDrift          = 'clock_drift',
    ConfigPendingFiles  = 'config_pending_files',
    StaticConfigDrift   = 'static_config_drift',
    AgentOutboxOverflow = 'agent_outbox_overflow',

    /** §7.2's abort. Critical, and never a user error — no operator edit clears it. */
    FlattenInvariantViolation = 'flatten_invariant_violation',
}

/**
 * The two severities, and deliberately not a third.
 *
 * An `info` issue is a contradiction: an issue is by definition something that needs attention, and
 * a collection that allowed one would fill the dashboard with rows nobody is meant to act on —
 * which is how an alarm surface becomes wallpaper. Every threshold in D60 is stated as a pair, so
 * two levels is what the specification actually describes.
 *
 * §9.5 maps them to colours **semantically** (`warning` / `error` in Nuxt UI's vocabulary), so
 * neither word ever appears in a locale file as itself — `libs/issue.ts` owns that map.
 */
export enum IssueSeverity {
    Warning  = 'warning',
    Critical = 'critical',
}
